import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, TextField, MenuItem, Select, FormControl, InputLabel,
  Typography, Box, Divider, CircularProgress, Chip, Alert
} from '@mui/material';
import { API } from '../../context/AuthContext';
import { fmt } from '../../utils';
import { useSnackbar } from 'notistack';

const METHODS = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta_credito', label: 'Tarjeta de crédito' },
  { value: 'tarjeta_debito', label: 'Tarjeta de débito' },
  { value: 'otro', label: 'Otro' }
];

export default function PaymentDialog({ open, invoice, onClose, onSave }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({
    amount: 0, method: 'transferencia', reference: '', bank: '', account: '', notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invoice) setForm(f => ({ ...f, amount: invoice.balance || invoice.total || 0 }));
  }, [invoice, open]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async () => {
    if (!form.amount || form.amount <= 0) return enqueueSnackbar('Monto inválido', { variant: 'error' });
    setLoading(true);
    try {
      await API.post('/payments', {
        client: invoice.client?._id || invoice.client,
        amount: parseFloat(form.amount),
        method: form.method,
        reference: form.reference,
        bank: form.bank,
        account: form.account,
        notes: form.notes,
        invoiceApplications: [{ invoiceId: invoice._id, amount: parseFloat(form.amount) }]
      });
      enqueueSnackbar('Pago aplicado exitosamente', { variant: 'success' });
      onSave();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al aplicar pago', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Aplicar pago</DialogTitle>
      <DialogContent dividers>
        {/* Invoice summary */}
        <Box sx={{ p: 2, background: 'rgba(107,33,168,0.05)', borderRadius: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Factura:</Typography>
            <Typography variant="body2" fontWeight={700} color="primary.main">{invoice.number}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Cliente:</Typography>
            <Typography variant="body2" fontWeight={600}>{invoice.clientSnapshot?.name || invoice.client?.name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Total:</Typography>
            <Typography variant="body2" fontWeight={600}>{fmt.currency(invoice.total)}</Typography>
          </Box>
          {invoice.paid > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Ya pagado:</Typography>
              <Typography variant="body2" fontWeight={600} color="success.main">{fmt.currency(invoice.paid)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" fontWeight={700}>Saldo pendiente:</Typography>
            <Typography variant="body1" fontWeight={800} color="warning.main">{fmt.currency(invoice.balance || invoice.total)}</Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Monto a aplicar *" type="number" value={form.amount}
              onChange={e => set('amount', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={`Máximo: ${fmt.currency(invoice.balance || invoice.total)}`} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Método de pago</InputLabel>
              <Select value={form.method} onChange={e => set('method', e.target.value)} label="Método de pago">
                {METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Referencia / Núm. de cheque" value={form.reference}
              onChange={e => set('reference', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Banco" value={form.bank} onChange={e => set('bank', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notas" value={form.notes} onChange={e => set('notes', e.target.value)} multiline rows={2} />
          </Grid>
        </Grid>

        {parseFloat(form.amount) > (invoice.balance || invoice.total) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            El monto supera el saldo pendiente. Se registrará como sobrepago.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" color="success" onClick={handleSubmit} disabled={loading || !form.amount}>
          {loading ? <CircularProgress size={20} /> : `Aplicar ${fmt.currency(parseFloat(form.amount) || 0)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
